import { FC, useCallback } from 'react';

import {
  Option,
  Typography,
} from '@beeline/design-system-react/extracted-styles';

import { findOrganizations } from 'src/api/spaces';
import { OrganizationInfo } from 'src/api/types';

import {
  AsyncAutocomplete,
  AsyncAutocompleteProps,
} from '../AsyncAutocomplete/AsyncAutocomplete';
import { TooltipWrapper } from '../TooltipWrapper/TooltipWrapper';

import classes from './OrganizationAutocomplete.module.scss';

const makeOption = (option: Option<OrganizationInfo>): JSX.Element | null => {
  return (
    <div className={classes.optionContainer}>
      <TooltipWrapper title={option.value.name}>
        <Typography variant='body2' ellipsis>
          {option.value.name}
        </Typography>
      </TooltipWrapper>
      {option.value.parent && (
        <TooltipWrapper title={option.value.parent.name}>
          <Typography variant='caption' ellipsis>
            {option.value.parent.name}
          </Typography>
        </TooltipWrapper>
      )}
    </div>
  );
};

const renderValue = (option: Option<OrganizationInfo>): string =>
  option.value.name ?? '';

export interface OrganizationAutocompleteProps
  extends Omit<
    AsyncAutocompleteProps<OrganizationInfo>,
    | 'type'
    | 'size'
    | 'noOptionsText'
    | 'renderValue'
    | 'fetchOptions'
    | 'fullWidth'
  > {
  employeeNumber?: string | undefined;
}

export const OrganizationAutocomplete: FC<OrganizationAutocompleteProps> = ({
  employeeNumber,
  ...rest
}) => {
  const fetchOptions = useCallback(
    async (
      search: string,
      limit: number,
      offset: number,
    ): Promise<Option<OrganizationInfo>[]> => {
      const organizations = await findOrganizations({
        searchText: search,
        itemLimit: limit,
        itemOffset: offset,
        employeeNumber,
      });
      return organizations.map(org => ({
        id: org.id,
        value: org,
      }));
    },
    [employeeNumber],
  );

  return (
    <AsyncAutocomplete
      {...rest}
      type='select'
      size='medium'
      fullWidth
      label='Подразделение'
      noOptionsText='Поздразделения не найдены'
      renderValue={renderValue}
      fetchOptions={fetchOptions}
      placeholder='Выберите подразделение'
      makeOption={makeOption}
      showTooltip
    />
  );
};
